const storageKey = "accountv2.cardFlow";
const storageTtlMs = 15 * 60 * 1000;

type StringDict = Record<string, string>;

export interface CardFlowContext {
  originRoute: string;
  routeParams?: StringDict;
  routeQuery?: StringDict;
  knownPaymentSourceIds: string[];
  createdAt: number;
  action?: string;
  updatedAt?: number;
}

export interface CardFlowContextPayload {
  originRoute: string;
  routeParams?: StringDict;
  routeQuery?: StringDict;
  knownPaymentSourceIds: string[];
}

const isBrowser = typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const readRawContext = (): CardFlowContext | null => {
  if (!isBrowser) {
    return null;
  }
  const raw = window.sessionStorage.getItem(storageKey);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as CardFlowContext;
    const now = Date.now();
    if (typeof parsed.createdAt !== "number" || now - parsed.createdAt > storageTtlMs) {
      window.sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return null;
  }
};

const writeContext = (context: CardFlowContext) => {
  if (!isBrowser) {
    return;
  }
  window.sessionStorage.setItem(storageKey, JSON.stringify(context));
};

export const setCardFlowContext = (payload: CardFlowContextPayload) => {
  if (!isBrowser) {
    return;
  }
  const context: CardFlowContext = {
    originRoute: payload.originRoute,
    routeParams: payload.routeParams,
    routeQuery: payload.routeQuery,
    knownPaymentSourceIds: payload.knownPaymentSourceIds,
    createdAt: Date.now()
  };
  writeContext(context);
};

export const loadCardFlowContext = (): CardFlowContext | null => {
  return readRawContext();
};

export const registerCardFlowAction = (action: string | null | undefined): CardFlowContext | null => {
  if (!action) {
    return null;
  }
  const trimmed = action.trim().toLowerCase();
  if (trimmed.length === 0) {
    return null;
  }
  const context = readRawContext();
  if (!context) {
    return null;
  }
  context.action = trimmed;
  context.updatedAt = Date.now();
  writeContext(context);
  return context;
};

export const clearCardFlowContext = () => {
  if (!isBrowser) {
    return;
  }
  window.sessionStorage.removeItem(storageKey);
};

export const toStringRecord = (record: Record<string, unknown>): StringDict => {
  const result: StringDict = {};
  for (const [key, value] of Object.entries(record ?? {})) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      result[key] = String(value);
      continue;
    }
    if (Array.isArray(value)) {
      const first = value.find(
        item => typeof item === "string" || typeof item === "number" || typeof item === "boolean"
      );
      if (first !== undefined) {
        result[key] = String(first);
      }
    }
  }
  return result;
};

export type CardFlowOutcome = "success" | "cancelled" | "error" | "unknown";

export const interpretCardFlowAction = (action: string | undefined): CardFlowOutcome => {
  if (!action) {
    return "unknown";
  }
  const normalized = action.trim().toLowerCase();
  if (normalized === "netaxept:error:cancelled") {
    return "cancelled";
  }
  if (
    normalized === "netaxept:success:card-added" ||
    normalized === "netaxept:success:card-added-previously-completed"
  ) {
    return "success";
  }
  if (normalized.startsWith("netaxept:error:")) {
    return "error";
  }
  if (normalized.startsWith("netaxept:success:")) {
    return "success";
  }
  return "unknown";
};
